import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { billingService } from '../../api/services/billing.service';
import { organizationsService } from '../../api/services/organizations.service';
import {
  formatMoney,
  TestAdjustmentPreviewDTO,
  TestAdjustmentResultDTO,
  TestCallReportDTO,
  TestNumberDTO,
} from '../../types/billing';
import { addDays, formatServerUtcAsIst, istNowLocal, istToday, istToServerUtc } from './closeBooksRules';
import { useLatestRequest } from './latestRequest';
import {
  ADJUST_FLOOR_IST,
  callsOf,
  confirmText,
  emptyForm,
  formErrors,
  formFrom,
  hasWork,
  maskKey,
  scopeText,
  stateLabel,
  stateTone,
  TestNumberForm,
  toRequest,
} from './testNumberRules';

const input =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';
const card = 'bg-white dark:bg-gray-800 rounded-lg shadow p-4';
const btn = 'px-3 py-2 rounded-md text-sm font-medium disabled:opacity-50';
const primary = `${btn} bg-blue-600 text-white hover:bg-blue-700`;
const secondary = `${btn} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200`;
const errBox = 'rounded-md bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300';

const inr = (n: number | null | undefined) => formatMoney(n ?? 0, 'INR');
const num = (n: number | null | undefined) => (n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

type Tab = 'numbers' | 'calls' | 'adjust';
interface Org { id: string; name: string; type?: string }

/**
 * Billing → Test numbers (platform staff). Our team's phones: a call with one of them is
 * never billed to the client; Teeoff is charged internally. Three tabs: the numbers (with
 * a business-wise scope), the business-wise test-call report, and "Adjust past charges"
 * for what was billed before a number was added.
 */
const TestNumbers: React.FC = () => {
  const [tab, setTab] = useState<Tab>('numbers');
  const [orgs, setOrgs] = useState<Org[]>([]);

  useEffect(() => {
    let live = true;
    Promise.all([organizationsService.getServiceProviders(), organizationsService.getAllBusinesses()])
      .then(([p, b]) => {
        if (!live) return;
        const all = [...((p as any)?.data || []), ...((b as any)?.data || [])] as Org[];
        setOrgs(all.sort((x, y) => (x.name || x.id).localeCompare(y.name || y.id)));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'numbers', label: 'Numbers' },
    { id: 'calls', label: 'Test calls' },
    { id: 'adjust', label: 'Adjust past charges' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Test numbers</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Our team's phones. A call between a business's line and one of these is never billed to the
          business or its provider; Teeoff is charged internally instead. Times are IST.
        </p>
      </div>
      <div className="border-b border-gray-200 dark:border-gray-700 flex gap-4" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`pb-2 text-sm font-medium border-b-2 ${tab === t.id
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'numbers' && <NumbersTab orgs={orgs} />}
      {tab === 'calls' && <CallsTab orgs={orgs} />}
      {tab === 'adjust' && <AdjustTab />}
    </div>
  );
};

// ── Numbers ────────────────────────────────────────────────────────────────────

const NumbersTab: React.FC<{ orgs: Org[] }> = ({ orgs }) => {
  const [rows, setRows] = useState<TestNumberDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<TestNumberDTO | 'new' | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    billingService.getTestNumbers().then((r) => {
      if (r.success && r.data) {
        setRows(r.data);
        setError(null);
      } else setError(r.error || 'Failed to load test numbers');
      setLoading(false);
    });
  }, []);
  useEffect(load, [load]);

  const end = async (n: TestNumberDTO) => {
    const r = await billingService.deactivateTestNumber(n.id);
    if (r.success) load();
    else setError(r.error || 'Could not end the test number');
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button className={primary} onClick={() => setEditing('new')}>Add test number</button>
      </div>
      {error && <div className={errBox}>{error}</div>}
      {editing && (
        <NumberEditor
          existing={editing === 'new' ? null : editing}
          orgs={orgs}
          onDone={(saved) => {
            setEditing(null);
            if (saved) load();
          }}
        />
      )}
      <div className={`${card} overflow-x-auto`}>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className={th}>Label</th>
              <th className={th}>Number</th>
              <th className={th}>Valid from</th>
              <th className={th}>Valid until</th>
              <th className={th}>Applies to</th>
              <th className={th}>Status</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading && <tr><td className={td} colSpan={7}>Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td className={td} colSpan={7}>No test numbers yet. Add your team's phones.</td></tr>
            )}
            {rows.map((n) => (
              <tr key={n.id}>
                <td className={td}>
                  <div className="font-medium">{n.label}</div>
                  {n.note && <div className="text-xs text-gray-500 dark:text-gray-400">{n.note}</div>}
                </td>
                <td className={td}>{n.number}</td>
                <td className={td}>{formatServerUtcAsIst(n.validFrom)}</td>
                <td className={td}>{n.validUntil ? formatServerUtcAsIst(n.validUntil) : 'Open-ended'}</td>
                <td className={`${td} whitespace-normal max-w-xs`}>{scopeText(n)}</td>
                <td className={td}>
                  <span className={`px-2 py-0.5 rounded text-xs ${stateTone[n.state]}`}>{stateLabel[n.state]}</span>
                </td>
                <td className={`${td} text-right space-x-2`}>
                  <button className={secondary} onClick={() => setEditing(n)}>Edit</button>
                  {n.state === 'ACTIVE' && (
                    <button className={secondary} onClick={() => end(n)} title="Calls before now stay test calls">
                      End now
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const NumberEditor: React.FC<{ existing: TestNumberDTO | null; orgs: Org[]; onDone: (saved: boolean) => void }> = ({
  existing, orgs, onDone,
}) => {
  const editing = existing !== null;
  const [form, setForm] = useState<TestNumberForm>(existing ? formFrom(existing) : emptyForm(istNowLocal()));
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const errors = formErrors(form, editing);
  const set = (patch: Partial<TestNumberForm>) => setForm((f) => ({ ...f, ...patch }));
  const visibleOrgs = orgs.filter((o) => !filter || (o.name || o.id).toLowerCase().includes(filter.toLowerCase()));

  const save = async () => {
    setTouched(true);
    if (Object.keys(errors).length) return;
    setSaving(true);
    const body = toRequest(form, editing);
    const r = existing ? await billingService.updateTestNumber(existing.id, body) : await billingService.createTestNumber(body);
    setSaving(false);
    if (r.success) onDone(true);
    else setError(r.error || 'Could not save');
  };

  const fieldError = (k: string) => touched && errors[k]
    ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors[k]}</p> : null;

  return (
    <div className={`${card} space-y-4`}>
      <h2 className="text-lg font-medium text-gray-900 dark:text-white">{editing ? `Edit ${existing!.label}` : 'Add test number'}</h2>
      {error && <div className={errBox}>{error}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="tn-number">Phone number</label>
          <input id="tn-number" className={`${input} w-full`} value={form.number} disabled={editing}
            placeholder="+91 98765 43210" onChange={(e) => set({ number: e.target.value })} />
          {editing && <p className="mt-1 text-xs text-gray-500">To change the number, end this one and add a new one.</p>}
          {fieldError('number')}
        </div>
        <div>
          <label className={labelCls} htmlFor="tn-label">Label / person</label>
          <input id="tn-label" className={`${input} w-full`} value={form.label}
            onChange={(e) => set({ label: e.target.value })} />
          {fieldError('label')}
        </div>
        <div>
          <label className={labelCls} htmlFor="tn-from">Valid from (IST)</label>
          <input id="tn-from" type="datetime-local" className={`${input} w-full`} value={form.validFrom}
            onChange={(e) => set({ validFrom: e.target.value })} />
          {fieldError('validFrom')}
        </div>
        <div>
          <label className={labelCls} htmlFor="tn-until">Valid until (IST, blank = open-ended)</label>
          <input id="tn-until" type="datetime-local" className={`${input} w-full`} value={form.validUntil}
            onChange={(e) => set({ validUntil: e.target.value })} />
          {fieldError('validUntil')}
        </div>
        <div className="md:col-span-2">
          <label className={labelCls} htmlFor="tn-note">Note</label>
          <input id="tn-note" className={`${input} w-full`} value={form.note} onChange={(e) => set({ note: e.target.value })} />
        </div>
      </div>

      <fieldset>
        <legend className={labelCls}>Applies to</legend>
        <div className="flex gap-6 text-sm text-gray-700 dark:text-gray-200">
          <label className="flex items-center gap-2">
            <input type="radio" name="tn-scope" checked={form.scopeMode === 'all'} onChange={() => set({ scopeMode: 'all' })} />
            All businesses
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="tn-scope" checked={form.scopeMode === 'selected'}
              onChange={() => set({ scopeMode: 'selected' })} />
            Selected businesses
          </label>
        </div>
        {form.scopeMode === 'selected' && (
          <div className="mt-2 space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">A provider covers every business below it.</p>
            <input className={`${input} w-full`} placeholder="Filter organisations" value={filter}
              onChange={(e) => setFilter(e.target.value)} aria-label="Filter organisations" />
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2 space-y-1">
              {visibleOrgs.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                  <input type="checkbox" checked={form.organizationIds.includes(o.id)}
                    onChange={(e) => set({
                      organizationIds: e.target.checked
                        ? [...form.organizationIds, o.id]
                        : form.organizationIds.filter((id) => id !== o.id),
                    })} />
                  {o.name || o.id}
                  {o.type === 'SERVICE' && <span className="text-xs text-gray-500">(provider)</span>}
                </label>
              ))}
              {visibleOrgs.length === 0 && <p className="text-sm text-gray-500">No organisations</p>}
            </div>
          </div>
        )}
        {fieldError('scope')}
      </fieldset>

      {editing && (
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
          <input type="checkbox" checked={!form.active} onChange={(e) => set({ active: !e.target.checked })} />
          Withdraw this entry (added by mistake: it then never matches, not even past calls)
        </label>
      )}

      <div className="flex gap-2 justify-end">
        <button className={secondary} onClick={() => onDone(false)}>Cancel</button>
        <button className={primary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
};

// ── Test calls (business-wise report) ──────────────────────────────────────────

const CallsTab: React.FC<{ orgs: Org[] }> = ({ orgs }) => {
  const [from, setFrom] = useState(`${istToday().slice(0, 7)}-01`);
  const [to, setTo] = useState(istToday());
  const [orgId, setOrgId] = useState('');
  const [report, setReport] = useState<TestCallReportDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState<string | null | undefined>(undefined);
  const beginRequest = useLatestRequest();

  useEffect(() => {
    if (!from || !to || to < from) return;
    const isLatest = beginRequest();
    setLoading(true);
    billingService.getTestCallReport({ from, to, organizationId: orgId }).then((r) => {
      if (!isLatest()) return;
      if (r.success && r.data) {
        setReport(r.data);
        setError(null);
      } else {
        setReport(null);
        setError(r.error || 'Failed to load the test-call report');
      }
      setLoading(false);
    });
  }, [from, to, orgId, beginRequest]);

  const calls = useMemo(() => callsOf(report?.callList || [], drill), [report, drill]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className={labelCls} htmlFor="tc-from">From (IST)</label>
          <input id="tc-from" type="date" className={input} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className={labelCls} htmlFor="tc-to">To (IST)</label>
          <input id="tc-to" type="date" className={input} value={to} min={from} max={addDays(istToday(), 0)}
            onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <label className={labelCls} htmlFor="tc-org">Business or provider</label>
          <select id="tc-org" className={input} value={orgId} onChange={(e) => { setOrgId(e.target.value); setDrill(undefined); }}>
            <option value="">All</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name || o.id}</option>)}
          </select>
        </div>
      </div>
      {error && <div className={errBox}>{error}</div>}
      {loading && <p className="text-sm text-gray-500">Loading…</p>}
      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['Test calls', num(report.calls)],
              ['Answered', num(report.answered)],
              ['Billed minutes', num(report.billedMinutes)],
              ['Internal cost (Teeoff)', inr(report.internalCost)],
            ].map(([k, v]) => (
              <div key={k} className={card}>
                <div className="text-xs text-gray-500 dark:text-gray-400">{k}</div>
                <div className="text-xl font-semibold text-gray-900 dark:text-white">{v}</div>
              </div>
            ))}
          </div>
          <div className={`${card} overflow-x-auto`}>
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">By business</h2>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className={th}>Business</th><th className={th}>Provider</th><th className={th}>Calls</th>
                  <th className={th}>Answered</th><th className={th}>Minutes</th><th className={th}>Internal cost</th>
                  <th className={th}>Test numbers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {report.byBusiness.length === 0 && <tr><td className={td} colSpan={7}>No test calls in this range.</td></tr>}
                {report.byBusiness.map((b) => (
                  <tr key={b.businessId || 'unknown'}
                    className={`cursor-pointer ${drill === (b.businessId || null) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    onClick={() => setDrill(drill === (b.businessId || null) ? undefined : (b.businessId || null))}>
                    <td className={td}>{b.businessName}</td>
                    <td className={td}>{b.providerName || '-'}</td>
                    <td className={td}>{num(b.calls)}</td>
                    <td className={td}>{num(b.answered)}</td>
                    <td className={td}>{num(b.billedMinutes)}</td>
                    <td className={td}>{inr(b.internalCost)}</td>
                    <td className={`${td} whitespace-normal`}>
                      {b.byNumber.map((n) => `${n.label} (${n.calls})`).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={`${card} overflow-x-auto`}>
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Calls{drill !== undefined ? ` — ${report.byBusiness.find((b) => (b.businessId || null) === drill)?.businessName || ''}` : ''}
              {report.truncated && ' (newest 1,000)'}
            </h2>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className={th}>When</th><th className={th}>Business</th><th className={th}>Direction</th>
                  <th className={th}>Test number</th><th className={th}>Seconds</th><th className={th}>Minutes</th>
                  <th className={th}>Internal cost</th><th className={th}>Recorded by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {calls.map((c) => (
                  <tr key={c.callId}>
                    <td className={td}>{formatServerUtcAsIst(c.occurredAt)}</td>
                    <td className={td}>{c.businessName}</td>
                    <td className={td}>{c.direction || '-'}{c.answered ? '' : ' (unanswered)'}</td>
                    <td className={td}>{c.label} {maskKey(c.numberKey)}</td>
                    <td className={td}>{num(c.billedSeconds)}</td>
                    <td className={td}>{num(c.billedMinutes)}</td>
                    <td className={td}>{inr(c.internalCost)}</td>
                    <td className={td}>{c.origin === 'ADJUSTMENT' ? 'Adjustment' : 'Charger'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ── Adjust past charges ────────────────────────────────────────────────────────

const AdjustTab: React.FC = () => {
  const [from, setFrom] = useState(ADJUST_FLOOR_IST);
  const [preview, setPreview] = useState<TestAdjustmentPreviewDTO | null>(null);
  const [result, setResult] = useState<TestAdjustmentResultDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const runPreview = async () => {
    setBusy(true);
    setResult(null);
    setConfirming(false);
    const r = await billingService.previewTestAdjustment(istToServerUtc(from) || undefined);
    setBusy(false);
    if (r.success && r.data) {
      setPreview(r.data);
      setError(null);
    } else {
      setPreview(null);
      setError(r.error || 'Failed to preview');
    }
  };

  const apply = async () => {
    if (!preview) return;
    setBusy(true);
    const r = await billingService.applyTestAdjustment({ from: istToServerUtc(from) || undefined, previewHash: preview.previewHash });
    setBusy(false);
    setConfirming(false);
    if (r.success && r.data) {
      setResult(r.data);
      setPreview(null);
      setError(null);
    } else {
      setError(r.error || 'Could not apply');
    }
  };

  return (
    <div className="space-y-4">
      <div className={`${card} space-y-3`}>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Finds charges already written for test calls (before the number was added) and takes them back:
          prepaid accounts get a refund to the grants it came from, postpaid accounts a credit adjustment that the
          next usage invoice nets. Charges already on an invoice are listed but never touched. Safe to repeat.
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className={labelCls} htmlFor="adj-from">Calls since (IST)</label>
            <input id="adj-from" type="datetime-local" className={input} value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <button className={secondary} onClick={runPreview} disabled={busy}>{busy && !confirming ? 'Working…' : 'Preview'}</button>
        </div>
      </div>
      {error && <div className={errBox}>{error}</div>}

      {preview && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['Test calls found', num(preview.callCount)],
              ['Charges to reverse', `${num(preview.pendingCharges)} · ${inr(preview.pendingAmount)}`],
              ['On an invoice (untouched)', `${num(preview.invoicedCharges)} · ${inr(preview.invoicedAmount)}`],
              ['Already adjusted', num(preview.alreadyAdjusted)],
            ].map(([k, v]) => (
              <div key={k} className={card}>
                <div className="text-xs text-gray-500 dark:text-gray-400">{k}</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">{v}</div>
              </div>
            ))}
          </div>
          <div className={`${card} overflow-x-auto`}>
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">By business</h2>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className={th}>Business</th><th className={th}>Provider</th><th className={th}>Calls</th>
                  <th className={th}>Payer</th><th className={th}>Type</th><th className={th}>To reverse</th>
                  <th className={th}>Invoiced</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {preview.byBusiness.length === 0 && <tr><td className={td} colSpan={7}>No test calls were charged.</td></tr>}
                {preview.byBusiness.flatMap((b) => (b.payers.length ? b.payers : [null]).map((p, i) => (
                  <tr key={`${b.businessId}-${p?.payerId || 'none'}`}>
                    <td className={td}>{i === 0 ? b.businessName : ''}</td>
                    <td className={td}>{i === 0 ? b.providerName || '-' : ''}</td>
                    <td className={td}>{i === 0 ? num(b.calls) : ''}</td>
                    <td className={td}>{p?.payerName || '-'}</td>
                    <td className={td}>{p?.billingType || '-'}</td>
                    <td className={td}>{p ? `${num(p.pendingCharges)} · ${inr(p.pendingAmount)}` : '-'}</td>
                    <td className={td}>{p && p.invoicedCharges ? `${num(p.invoicedCharges)} · ${inr(p.invoicedAmount)}` : '-'}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
          <div className={`${card} overflow-x-auto`}>
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Calls</h2>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className={th}>When</th><th className={th}>Business</th><th className={th}>Direction</th>
                  <th className={th}>Test number</th><th className={th}>Charges</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {preview.calls.map((c) => (
                  <tr key={c.callId}>
                    <td className={td}>{formatServerUtcAsIst(c.occurredAt)}</td>
                    <td className={td}>{c.businessName}</td>
                    <td className={td}>{c.direction}</td>
                    <td className={td}>{c.testNumberLabel}</td>
                    <td className={`${td} whitespace-normal`}>
                      {c.charges.length === 0 ? 'none' : c.charges.map((x) =>
                        `${x.payerName} ${inr(x.amount)} (${x.status.toLowerCase().replace('_', ' ')})`).join('; ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasWork(preview) && !confirming && (
            <div className="flex justify-end">
              <button className={primary} onClick={() => setConfirming(true)}>Apply…</button>
            </div>
          )}
          {confirming && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-4 space-y-3" role="alert">
              <p className="text-sm text-amber-900 dark:text-amber-200">{confirmText(preview, inr)}</p>
              <div className="flex gap-2 justify-end">
                <button className={secondary} onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
                <button className={primary} onClick={apply} disabled={busy}>{busy ? 'Applying…' : 'Confirm and apply'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className={`${card} space-y-2`}>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Done</h2>
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Reversed {num(result.reversed)} charge(s), {inr(result.reversedAmount)}; recorded {num(result.internalRowsWritten)} test
            call(s) against Teeoff.
            {result.invoicedUntouched.length > 0 && ` ${result.invoicedUntouched.length} invoiced charge(s) left for correction on their invoice.`}
          </p>
          {result.byPayer.length > 0 && (
            <ul className="text-sm text-gray-700 dark:text-gray-200 list-disc pl-5">
              {result.byPayer.map((p) => (
                <li key={`${p.payerId}-${p.businessId}`}>{p.payerName} for {p.businessName}: {num(p.doneCharges)} · {inr(p.doneAmount)}</li>
              ))}
            </ul>
          )}
          {result.failures.length > 0 && (
            <div className={errBox}>
              {result.failures.map((f, i) => <div key={i}>{f.transactionId ? `#${f.transactionId}: ` : ''}{f.reason}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TestNumbers;
